// src/components/sections/Hero.jsx - Refactored with Button component
import React, { useState } from 'react';
import { ArrowRight, Scale, Shield, Cpu } from 'lucide-react';
import Button from '../ui/Button';

const HeroSection = () => {
  const [activeTab, setActiveTab] = useState('features');
  
  // Features data
  const featuresData = [
    {
      icon: <Scale className="h-10 w-10 text-accent mb-3" />,
      title: "Legal Excellence",
      description: "Personalized legal strategies built on deep expertise and a commitment to results."
    },
    {
      icon: <Shield className="h-10 w-10 text-accent mb-3" />,
      title: "Client Protection",
      description: "Vigorous advocacy that safeguards your interests at every turn."
    },
    {
      icon: <Cpu className="h-10 w-10 text-accent mb-3" />,
      title: "AI-Powered Legal",
      description: "Advanced technology integration for faster, more accurate legal solutions."
    },
    {
      icon: (
        <svg className="h-10 w-10 text-accent mb-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 14L8 10H16L12 14Z" fill="currentColor" />
          <path d="M3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Transparent Process",
      description: "Clear communication and predictable legal pathways from start to finish."
    }
  ];
  
  // Core values data - Updated with gold/yellow icons to match "Our Approach" section
  const coreValuesData = [
    {
      icon: (
        <svg className="h-10 w-10 text-accent mb-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Execution",
      description: "We define success through real-world client outcomes rather than billable hours."
    },
    {
      icon: (
        <svg className="h-10 w-10 text-accent mb-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Innovation",
      description: "We are harnessing cutting-edge technologies to solve client challenges effectively."
    },
    {
      icon: (
        <svg className="h-10 w-10 text-accent mb-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Integrity",
      description: "We uphold the highest standards of ethics and transparency while honoring our commitments."
    },
    {
      icon: (
        <svg className="h-10 w-10 text-accent mb-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Trust",
      description: "Trust is the cornerstone of strong legal representation and we honor that with unwavering dedication."
    }
  ];

  return (
    <div className="relative bg-gradient-to-r from-primary via-primary-dark to-primary overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 25px 25px, white 2%, transparent 0%)",
          backgroundSize: "50px 50px"
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative z-10">
        <div className="flex flex-col md:flex-row items-center">
          <div className="w-full md:w-1/2 mb-12 md:mb-0">
            <div className="md:pr-12">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
                Legal Excellence <span className="text-accent">Powered by Innovation</span>
              </h1>
              <p className="text-gray-300 text-lg md:text-xl mb-8">
                AMV Law combines traditional legal expertise with cutting-edge technology to deliver superior outcomes for our clients across India.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {/* REFACTORED: Using Button component instead of custom styling */}
                <Button 
                  href="/contact"
                  variant="accent"
                  size="lg"
                  className="inline-flex items-center"
                >
                  Consult With Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                {/* REFACTORED: Using Button component with outline variant */}
                <Button
                  href="/services"
                  variant="text"
                  size="lg"
                  className="text-white border border-white hover:bg-white/10"
                >
                  Our Services
                </Button>
              </div>
            </div>
          </div>
          
          <div className="w-full md:w-1/2">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 shadow-xl border border-white/10">
              {/* Tab buttons */}
              <div className="flex border-b border-white/10 mb-6">
                <button 
                  onClick={() => setActiveTab('features')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'features' 
                      ? 'text-accent border-b-2 border-accent' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  Our Approach
                </button>
                <button 
                  onClick={() => setActiveTab('values')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'values' 
                      ? 'text-accent border-b-2 border-accent' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  Core Values
                </button>
              </div>
              
              {/* Features grid */}
              {activeTab === 'features' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {featuresData.map((feature, index) => (
                    <div key={index} className="bg-primary-dark p-5 rounded-lg">
                      {feature.icon}
                      <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                      <p className="text-gray-300 text-sm">{feature.description}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Core Values grid - Updated to use accent color (gold/yellow) for icons */}
              {activeTab === 'values' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {coreValuesData.map((value, index) => (
                    <div key={index} className="bg-primary-dark p-5 rounded-lg">
                      {value.icon}
                      <h3 className="text-white font-semibold text-lg mb-2">{value.title}</h3>
                      <p className="text-gray-300 text-sm">{value.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;